import { useEffect, useMemo } from "react";

import { Card, FontIcon } from "@ezmid/components-library";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as yup from "yup";

import { useGetBackOfficeListProductsQuery } from "../../../api/products.generated";
import {
  EntitySubscriptionPackage,
  useUpdateSubscriptionPackageByIdMutation,
} from "../../../api/subscription-packages.generated";
import { Option } from "../../../components/hookFormComponents/SelectInputField";
import { handleValidationErrors } from "../../../utils/validationErrorHandler";
import { PageLoader } from "../../loader/PageLoader";
import { SubscriptionPackageDetailForm } from "../molecules/SubscriptionPackageDetailForm";

type FormValues = {
  name: string;
  description: string;
  plan: string;
  duration: number;
  credits: number;
  status: string;
  productId: string;
};

type Props = {
  currentData?: EntitySubscriptionPackage;
  isFetching: boolean;
};

export function SubscriptionPackageDetailPageContent({
  currentData,
  isFetching,
}: Props) {
  const { t } = useTranslation();
  const [updatePackage] = useUpdateSubscriptionPackageByIdMutation();
  const { currentData: productsData } = useGetBackOfficeListProductsQuery({
    size: 100,
  });

  const FormSchema = yup.object().shape({
    name: yup.string().required(t("form.name.required")),
    description: yup.string(),
    plan: yup.string().required(t("form.plan.required")),
    duration: yup
      .number()
      .min(1, t("form.duration.min"))
      .required(t("form.duration.required")),
    credits: yup.number().min(0, t("form.credits.min")),
    status: yup.string().required(),
    productId: yup.string().required(t("form.product.required")),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    getValues,
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      name: currentData?.name || "",
      description: currentData?.description || "",
      plan: currentData?.plan || "sp-basic",
      duration: currentData?.duration || 1,
      credits: currentData?.credits || 0,
      status: currentData?.status || "sps-active",
      productId: currentData?.product?.id || "",
    },
    mode: "all",
    resolver: yupResolver(FormSchema),
  });

  useEffect(
    function resetFormDataAfterFetch() {
      if (!isFetching && !!currentData) {
        reset({
          name: currentData?.name || "",
          description: currentData?.description || "",
          plan: currentData?.plan || "sp-basic",
          duration: currentData?.duration || 1,
          credits: currentData?.credits || 0,
          status: currentData?.status || "sps-active",
          productId: currentData?.product?.id || "",
        });
      }
    },
    [currentData]
  );

  const productOptions: Option[] = useMemo(
    () =>
      productsData?.items?.map((product) => ({
        value: product.id || "",
        label:
          product.vendorProductID || product.description || product.id || "",
      })) || [],
    [productsData]
  );

  const planOptions: Option[] = [
    { value: "sp-basic", label: "Basic" },
    { value: "sp-premium", label: "Premium" },
  ];

  const statusOptions: Option[] = [
    { value: "sps-active", label: t("app.status.subscriptionPackage.active") },
    {
      value: "sps-inactive",
      label: t("app.status.subscriptionPackage.inactive"),
    },
  ];

  const onSubmit = () => {
    const { name, description, plan, duration, credits, status, productId } =
      getValues();
    updatePackage({
      id: currentData?.id || "",
      subscriptionPackageUpdateRequest: {
        name,
        description,
        plan: plan as "sp-basic" | "sp-premium",
        duration,
        credits,
        status: status as "sps-active" | "sps-inactive",
        productId: Number(productId),
      },
    })
      .unwrap()
      .then(() => {
        reset({
          name,
          description,
          plan,
          duration,
          credits,
          status,
          productId,
        });
        toast.success(t("app.subscriptionPackage.edit.success"));
      })
      .catch((error) => {
        const { hasFieldErrors, errorMessage } = handleValidationErrors(
          error,
          setError
        );
        if (!hasFieldErrors) {
          toast.warning(
            errorMessage || t("app.subscriptionPackage.edit.failed")
          );
        }
      });
  };

  const isSubmitButtonDisabled = !isDirty || isFetching;

  const defaultPlanValue =
    planOptions.find((o) => o.value === currentData?.plan) || null;
  const defaultStatusValue =
    statusOptions.find((o) => o.value === currentData?.status) || null;
  const defaultProductValue =
    productOptions.find((o) => o.value === currentData?.product?.id) || null;

  return isFetching ? (
    <PageLoader />
  ) : (
    <div className="w-full min-w-full flex justify-center">
      <div className="w-1/2 overflow-auto">
        <Card
          size="fill"
          shadows="xl"
          headerText={t("app.page.subscriptions.heading")}
          headerIcon={
            <FontIcon
              marginTop={4}
              customIcon={<FontAwesomeIcon icon={faInfoCircle} />}
            />
          }
        >
          <SubscriptionPackageDetailForm
            control={control}
            errors={errors}
            handleSubmit={handleSubmit(onSubmit)}
            onSubmit={onSubmit}
            isSubmitButtonDisabled={isSubmitButtonDisabled}
            planOptions={planOptions}
            statusOptions={statusOptions}
            productOptions={productOptions}
            defaultPlanValue={defaultPlanValue}
            defaultStatusValue={defaultStatusValue}
            defaultProductValue={defaultProductValue}
          />
        </Card>
      </div>
    </div>
  );
}
